// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Settings, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UserProfileData, ProfileStats } from '../hooks/useUserProfile';
import { useLoginPrompt } from './LoginPrompt';

interface ProfileHeaderProps {
  profile: UserProfileData;
  stats: ProfileStats | undefined;
  isCurrentUser: boolean;
  isFollowing?: boolean;
}

export function ProfileHeader({ profile, stats, isCurrentUser, isFollowing = false }: ProfileHeaderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showLoginPrompt } = useLoginPrompt();
  const isAuthenticated = !!user;

  const name = profile.first_name && profile.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile.username || 'Unknown';

  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const toggleFollow = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Must be logged in');

      if (isFollowing) {
        const { error } = await supabase
          .from('profile_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('followed_id', profile.id);

        if (error) throw error;
        return { action: 'unfollowed' };
      } else {
        const { error } = await supabase
          .from('profile_follows')
          .insert({
            follower_id: user.id,
            followed_id: profile.id,
          });

        if (error) throw error;
        return { action: 'followed' };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['profile-stats', profile.id] });
      queryClient.invalidateQueries({ queryKey: ['is-following', user?.id, profile.id] });
      queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
      toast.success(result.action === 'followed' ? 'Following!' : 'Unfollowed');
    },
    onError: () => {
      toast.error('Failed to update follow status');
    },
  });

  const handleFollowClick = () => {
    if (!isAuthenticated) {
      showLoginPrompt('follow this athlete');
      return;
    }
    toggleFollow.mutate();
  };

  return (
    <div className="bg-card px-4 py-6 border-b border-border">
      {/* Avatar */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <Avatar className="h-24 w-24">
            <AvatarImage src={profile.avatar_url || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          {isCurrentUser && (
            <button className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-2">
              <Camera className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Name and Username */}
      <div className="text-center mb-3">
        <h1 className="text-xl font-bold text-foreground">{name}</h1>
        {profile.username && (
          <p className="text-muted-foreground text-sm">@{profile.username}</p>
        )}
      </div>

      {/* Bio */}
      {profile.bio && (
        <p className="text-center text-foreground text-sm mb-4 max-w-xs mx-auto">
          {profile.bio}
        </p>
      )}

      {/* Location/Info */}
      {(profile.city || profile.state || profile.grad_year) && (
        <p className="text-center text-muted-foreground text-xs mb-4">
          {[profile.city, profile.state].filter(Boolean).join(', ')}
          {profile.grad_year && ` • Class of ${profile.grad_year}`}
        </p>
      )}

      {/* Stats Row */}
      <div className="flex justify-center gap-8 mb-4">
        <div className="text-center">
          <p className="text-lg font-bold text-foreground">{stats?.postsCount || 0}</p>
          <p className="text-xs text-muted-foreground">Posts</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-foreground">{stats?.followersCount || 0}</p>
          <p className="text-xs text-muted-foreground">Followers</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-foreground">{stats?.followingCount || 0}</p>
          <p className="text-xs text-muted-foreground">Following</p>
        </div>
      </div>

      {/* Action Button */}
      {isCurrentUser ? (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Edit Profile
            </Link>
          </Button>
        </div>
      ) : (
        <div className="flex justify-center">
          <Button
            variant={isFollowing ? "outline" : "default"}
            size="sm"
            onClick={handleFollowClick}
            disabled={toggleFollow.isPending}
          >
            {toggleFollow.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isFollowing ? (
              'Following'
            ) : (
              'Follow'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
